import { useQuery } from '@tanstack/react-query';
import type { RegistrarKind } from '@simsm/shared-types';
import { listReportingLocations, type ReportingLocation } from './api';

/**
 * Whether the active Registrar's type is known yet: `loading` until the route has
 * answered, `error` if it failed with no answer held, or answered with no type this
 * bundle knows, `ready` once it named one of the six. Only `ready` lets the dialog
 * offer reasons, and `ready` always comes with a type
 * (docs/shape-ops-contract-codes.md §Decision 8;
 * docs/shape-ops-registrar-kind-required.md §Decision 6).
 */
export type RegistrarKindState = 'loading' | 'error' | 'ready';

/**
 * The storage locations the operator may report for (caiet UC50 flux step 3),
 * and the active Registrar's type, which decides the reasons the report may carry
 * (UC50-15). They change about as often as a Registrar opens a branch, so they
 * are held for the session rather than refetched per screen; a Registrar switch
 * resets every query but the session's, so they are read again for the new one.
 */
export function useReportingLocations(): {
  locations: ReportingLocation[];
  /** Null until the state is `ready`. */
  registrarKind: RegistrarKind | null;
  registrarKindState: RegistrarKindState;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['reporting-locations'],
    queryFn: listReportingLocations,
    staleTime: 5 * 60_000,
  });
  const registrarKind = query.data?.registrarKind ?? null;
  return {
    locations: query.data?.locations ?? [],
    registrarKind,
    // An answer already held stays good when a background refetch fails: the
    // query is reset on a Registrar switch, so it is this Registrar's.
    registrarKindState: registrarKind ? 'ready' : query.data || query.isError ? 'error' : 'loading',
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
